import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

/**
 * Sections of the notes list: pinned notes first, then today, the last seven
 * days, then one section per month, newest first.
 */
export type NoteSectionKey = 'pinned' | 'today' | 'thisWeek' | `month:${string}`

export interface NoteSection<T> {
  key: NoteSectionKey
  data: T[]
}

const sectionRank = (key: NoteSectionKey) =>
  key === 'pinned' ? 0 : key === 'today' ? 1 : key === 'thisWeek' ? 2 : 3

// "Cette semaine" means the six days before today, whatever the weekday:
// a calendar week would empty it every Monday (and break on Sundays).
const startOfLastSevenDays = (now: dayjs.Dayjs) => now.startOf('day').subtract(6, 'day')

/**
 * Groups notes into sections. The API returns the newest notes first; that
 * order is kept inside each section, and empty sections are dropped.
 */
export function groupNotesByDate<T extends { createdAt: string; isPinned?: boolean }>(
  items: T[],
  now: dayjs.Dayjs = dayjs(),
): NoteSection<T>[] {
  const sections = new Map<NoteSectionKey, T[]>()
  const push = (key: NoteSectionKey, item: T) => {
    const list = sections.get(key)
    if (list) list.push(item)
    else sections.set(key, [item])
  }

  items.forEach((item) => {
    if (item.isPinned) return push('pinned', item)
    const date = dayjs(item.createdAt)
    if (date.isSame(now, 'day')) return push('today', item)
    if (!date.isBefore(startOfLastSevenDays(now))) return push('thisWeek', item)
    push(`month:${date.format('YYYY-MM')}`, item)
  })

  return [...sections.entries()]
    .sort(([a], [b]) => sectionRank(a) - sectionRank(b) || (a < b ? 1 : a > b ? -1 : 0))
    .map(([key, data]) => ({ key, data }))
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

/** Title of a month section, e.g. "Août 2026" (in the current dayjs language). */
export function formatNoteMonth(key: NoteSectionKey): string {
  return capitalize(dayjs(`${key.slice('month:'.length)}-01`).format('MMMM YYYY'))
}

/**
 * Date shown under a note: "Aujourd'hui · 14:48", "Hier · 19:51",
 * "Mardi · 19:51" in the last seven days, then "12 août", or "12 août 2025"
 * for another year.
 */
export function formatNoteDate(
  createdAt: string,
  labels: { today: string; yesterday: string },
  now: dayjs.Dayjs = dayjs(),
): string {
  const date = dayjs(createdAt)
  const time = date.format('HH:mm')
  if (date.isSame(now, 'day')) return `${labels.today} · ${time}`
  if (date.isSame(now.subtract(1, 'day'), 'day')) return `${labels.yesterday} · ${time}`
  if (!date.isBefore(startOfLastSevenDays(now)))
    return `${capitalize(date.format('dddd'))} · ${time}`
  return date.isSame(now, 'year') ? date.format('D MMMM') : date.format('D MMMM YYYY')
}

// Letters, digits, accents (Latin-1 ranges), "_" and "-". Written without
// \p{L}, which not every JavaScript engine of the app understands.
const HASHTAG = /#[A-Za-z0-9_À-ÖØ-öø-ÿ-]+/g

/** The hashtags typed in a note, and its text without them (for the preview). */
export function splitNoteHashtags(content: string): { text: string; hashtags: string[] } {
  const hashtags = Array.from(new Set(content.match(HASHTAG) ?? []))
  const text = content
    .replace(HASHTAG, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .trim()
  return { text, hashtags }
}

/**
 * Date of a line of « Historique avec X »: "Aujourd'hui", "Hier", "Lundi" in
 * the last seven days, "3 sept." (with the year if another one), and for what
 * is planned "Dans 14 jours".
 */
export function formatHistoryDate(
  date: string,
  labels: { today: string; yesterday: string },
  now: dayjs.Dayjs = dayjs(),
): string {
  const day = dayjs(date)
  if (day.isAfter(now)) return capitalize(day.from(now))
  if (day.isSame(now, 'day')) return labels.today
  if (day.isSame(now.subtract(1, 'day'), 'day')) return labels.yesterday
  if (!day.isBefore(startOfLastSevenDays(now))) return capitalize(day.format('dddd'))
  return day.isSame(now, 'year') ? day.format('D MMM') : day.format('D MMM YYYY')
}
